import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, Minus, Plus } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useCart } from "../context/CartContext";

const WHATSAPP_NUMBER = "201037255878";
const ADULT_SIZES = ["S", "M", "L", "XL", "XXL", "XXXL"];
const KIDS_SIZES = ["XXS", "XS", "S", "M", "L", "XL", "XXL"];
const CATEGORIES = [
  { key: "football", tk: "catFootball", price: 40 },
  { key: "retro", tk: "catRetro", price: 50 },
  { key: "kids", tk: "catKids", price: 40 },
  { key: "nba", tk: "catNba", price: 50 },
  { key: "nbaLegends", tk: "catNbaLegends", price: 60 },
  { key: "tracksuit", tk: "catTracksuit", price: 65 },
  { key: "f1tee", tk: "catF1Tee", price: 50 },
  { key: "f1polo", tk: "catF1Polo", price: 60 },
];
const PATCH_OPTIONS = [
  { key: "none", tk: "patchNone" },
  { key: "champions", tk: "patchChampions" },
  { key: "campionato", tk: "patchCampionato" },
];
// Per-unit blocks (quantity > 1): all jersey categories except tracksuits
const UNIT_CATEGORIES = CATEGORIES.filter((c) => c.key !== "tracksuit");

export const OrderModal = ({ isOpen, onClose, product }) => {
  const { t } = useLanguage();
  const { addItem, setCartOpen } = useCart();
  const [form, setForm] = useState({
    category: "",
    team: "",
    version: "",
    size: "M",
    quantity: 1,
    personalization: "",
    notes: "",
    patch: "none",
  });
  const [error, setError] = useState("");

  // Prefill jersey + category from selected product
  useEffect(() => {
    if (isOpen) {
      setForm({
        category: product?.category || "",
        team: "",
        version: "",
        size: "M",
        quantity: 1,
        personalization: "",
        notes: "",
        patch: "none",
      });
      setError("");
      setExtraUnits([]);
    }
  }, [isOpen, product]);

  // One extra configurable block per additional jersey (quantity > 1)
  const [extraUnits, setExtraUnits] = useState([]);
  useEffect(() => {
    const needed = Math.max(0, form.quantity - 1);
    setExtraUnits((prev) => {
      if (prev.length === needed) return prev;
      if (prev.length > needed) return prev.slice(0, needed);
      const next = [...prev];
      while (next.length < needed) next.push({ category: "", team: "", version: "", personalization: "", patch: "none", notes: "" });
      return next;
    });
  }, [form.quantity]);
  const setUnitField = (i, field, value) =>
    setExtraUnits((prev) => prev.map((u, idx) => (idx === i ? { ...u, [field]: value } : u)));

  const isKids = form.category === "kids";
  const availableSizes = isKids ? KIDS_SIZES : ADULT_SIZES;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // When category flips between kids and adult, reset size to "M" (always in both lists)
    if (name === "category") {
      setForm((prev) => ({ ...prev, category: value, size: "M" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const changeQty = (delta) => {
    setForm((prev) => ({ ...prev, quantity: Math.max(1, Math.min(99, prev.quantity + delta)) }));
  };

  const buildItem = (f, qty) => {
    const cat = CATEGORIES.find((c) => c.key === f.category);
    const patchOpt = PATCH_OPTIONS.find((p) => p.key === f.patch);
    const jerseyText = [f.team.trim(), f.version.trim()].filter(Boolean).join(" — ");
    return {
      category: f.category,
      categoryLabel: cat ? t(`order.${cat.tk}`) : f.category,
      price: cat ? cat.price : 0,
      jersey: jerseyText,
      size: form.size,
      quantity: qty,
      personalization: f.personalization,
      notes: f.notes,
      patch: patchOpt ? patchOpt.key : "none",
      patchLabel: patchOpt && patchOpt.key !== "none" ? t(`order.${patchOpt.tk}`) : "",
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.team.trim() || !form.category) {
      setError(t("order.required"));
      return;
    }
    if (extraUnits.some((u) => !u.team.trim() || !u.category)) {
      setError(t("order.unitFillError"));
      return;
    }
    // Quantity > 1: each jersey is added as its own line with its own configuration
    addItem(buildItem(form, 1));
    extraUnits.forEach((u) => addItem(buildItem(u, 1)));
    onClose();
    setCartOpen(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
          data-testid="order-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-sm p-8 max-h-[90vh] overflow-y-auto"
            data-testid="order-modal"
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
              data-testid="order-modal-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#009A49] mb-2 font-body">
              Locker Room
            </p>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white tracking-wide mb-4">
              {t("order.title")}
            </h3>

            {/* Discount banner */}
            <div className="mb-6 px-4 py-3 bg-[#009A49]/15 border border-[#009A49]/40 rounded-sm">
              <p className="text-[#009A49] font-body text-sm font-semibold text-center" data-testid="order-discount-banner">
                {t("order.discountBanner")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {extraUnits.length > 0 && (
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#009A49] font-body -mb-2" data-testid="unit-label-1">
                  {t("order.jerseyWord")} 1
                </p>
              )}
              {/* Category */}
              <div>
                <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                  {t("order.category")}
                </label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors"
                  data-testid="order-category-select"
                >
                  <option value="">{t("order.selectCategory")}</option>
                  {CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {t(`order.${c.tk}`)} — €{c.price}
                    </option>
                  ))}
                </select>
              </div>

              {/* Team or Driver */}
              <div>
                <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                  {t("order.teamOrDriver")}
                </label>
                <input
                  type="text"
                  name="team"
                  value={form.team}
                  onChange={handleChange}
                  placeholder={t("order.teamPh")}
                  className="w-full px-4 py-3.5 bg-transparent border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-500"
                  data-testid="order-team-input"
                />
              </div>

              {/* Version (Home / Away / Retro…) */}
              <div>
                <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                  {t("order.version")}
                </label>
                <input
                  type="text"
                  name="version"
                  value={form.version}
                  onChange={handleChange}
                  placeholder={t("order.versionPh")}
                  className="w-full px-4 py-3.5 bg-transparent border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-500"
                  data-testid="order-version-input"
                />
              </div>

              {/* Size */}
              <div>
                <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                  {t("order.size")}
                  {isKids && (
                    <span className="ml-2 text-[10px] tracking-normal normal-case text-[#009A49] font-body">
                      · {t("order.kidsSizesNote")}
                    </span>
                  )}
                </label>
                <div className="flex flex-wrap gap-2" data-testid={isKids ? "order-size-list-kids" : "order-size-list-adult"}>
                  {availableSizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({ ...form, size: s })}
                      className={`px-4 py-2 rounded-sm font-body text-sm font-semibold transition-colors ${
                        form.size === s
                          ? "bg-[#009A49] text-white"
                          : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                      }`}
                      data-testid={`order-size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                  {t("order.quantity")}
                </label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => changeQty(-1)}
                    className="w-10 h-10 flex items-center justify-center border border-white/10 rounded-sm text-white hover:border-[#009A49] hover:text-[#009A49] transition-colors"
                    data-testid="order-qty-minus"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span
                    className="min-w-[3rem] text-center font-heading text-2xl text-white"
                    data-testid="order-qty-value"
                  >
                    {form.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => changeQty(1)}
                    className="w-10 h-10 flex items-center justify-center border border-white/10 rounded-sm text-white hover:border-[#009A49] hover:text-[#009A49] transition-colors"
                    data-testid="order-qty-plus"
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Notes / special requests (optional) */}
              <div>
                <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                  {t("cart.notes")}
                  <span className="ml-2 text-[10px] tracking-normal normal-case text-zinc-600 font-body">
                    · {t("order.optional")}
                  </span>
                </label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder={t("cart.notesPh")}
                  rows={2}
                  className="w-full px-4 py-3 bg-transparent border border-white/10 rounded-sm text-white font-body resize-none focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600"
                  data-testid="order-notes-input"
                />
              </div>

              <div>
                <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                  {t("order.personalization")}
                </label>
                <input
                  type="text"
                  name="personalization"
                  value={form.personalization}
                  onChange={handleChange}
                  placeholder={t("order.personalizationPh")}
                  className="w-full px-4 py-3 bg-transparent border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-600"
                  data-testid="order-personalization-input"
                />
              </div>

              {/* Patch (optional, single selection) — available for every jersey */}
              <div data-testid="order-patch-group">
                  <label className="block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body">
                    {t("order.patch")}
                    <span className="ml-2 text-[10px] tracking-normal normal-case text-zinc-600 font-body">
                      · {t("order.optional")}
                    </span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {PATCH_OPTIONS.map((opt) => {
                      const selected = form.patch === opt.key;
                      return (
                        <label
                          key={opt.key}
                          className={`flex items-center gap-3 px-4 py-3 rounded-sm border cursor-pointer transition-colors ${
                            selected
                              ? "bg-[#009A49]/15 border-[#009A49] text-white"
                              : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                          }`}
                          data-testid={`order-patch-${opt.key}`}
                        >
                          <input
                            type="radio"
                            name="patch"
                            value={opt.key}
                            checked={selected}
                            onChange={handleChange}
                            className="w-4 h-4 accent-[#009A49] cursor-pointer"
                          />
                          <span className="font-body text-sm">{t(`order.${opt.tk}`)}</span>
                        </label>
                      );
                    })}
                  </div>
              </div>

              {/* Per-jersey blocks — one per extra unit when quantity > 1 */}
              {extraUnits.map((unit, i) => {
                const n = i + 2;
                const unitLabel = "block text-sm font-bold tracking-[0.15em] uppercase text-white mb-2 font-body";
                const unitInput = "w-full px-4 py-3.5 bg-transparent border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-500";
                return (
                  <div key={n} className="border-2 border-[#009A49]/60 rounded-sm p-5 space-y-5 bg-[#009A49]/10" data-testid={`unit-block-${n}`}>
                    {/* Numbered jersey header */}
                    <div className="flex items-center gap-3 pb-3 border-b border-[#009A49]/30" data-testid={`unit-label-${n}`}>
                      <span className="w-9 h-9 rounded-full bg-[#009A49] text-white font-heading text-lg font-bold flex items-center justify-center flex-shrink-0">{n}</span>
                      <p className="text-base md:text-lg font-bold tracking-[0.25em] uppercase text-[#009A49] font-body">
                        {t("order.jerseyWord")} {n}
                      </p>
                    </div>
                    <div>
                      <label className={unitLabel}>{t("order.category")}</label>
                      <select
                        value={unit.category}
                        onChange={(e) => setUnitField(i, "category", e.target.value)}
                        className="w-full px-4 py-3.5 bg-[#0A0A0A] border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors"
                        data-testid={`unit-category-${n}`}
                      >
                        <option value="">{t("order.selectCategory")}</option>
                        {UNIT_CATEGORIES.map((c) => (
                          <option key={c.key} value={c.key}>
                            {t(`order.${c.tk}`)} — €{c.price}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={unitLabel}>{t("order.teamOrDriver")}</label>
                      <input
                        type="text"
                        value={unit.team}
                        onChange={(e) => setUnitField(i, "team", e.target.value)}
                        placeholder={t("order.teamPh")}
                        className={unitInput}
                        data-testid={`unit-team-${n}`}
                      />
                    </div>
                    <div>
                      <label className={unitLabel}>{t("order.version")}</label>
                      <input
                        type="text"
                        value={unit.version}
                        onChange={(e) => setUnitField(i, "version", e.target.value)}
                        placeholder={t("order.versionPh")}
                        className={unitInput}
                        data-testid={`unit-version-${n}`}
                      />
                    </div>
                    <div>
                      <label className={unitLabel}>{t("order.personalization")}</label>
                      <input
                        type="text"
                        value={unit.personalization}
                        onChange={(e) => setUnitField(i, "personalization", e.target.value)}
                        placeholder={t("order.personalizationPh")}
                        className={unitInput}
                        data-testid={`unit-personalization-${n}`}
                      />
                    </div>
                    <div>
                      <label className={unitLabel}>{t("order.patch")}</label>
                      <select
                        value={unit.patch}
                        onChange={(e) => setUnitField(i, "patch", e.target.value)}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-sm text-white font-body focus:outline-none focus:border-[#009A49] transition-colors"
                        data-testid={`unit-patch-${n}`}
                      >
                        {PATCH_OPTIONS.map((o) => (
                          <option key={o.key} value={o.key}>{t(`order.${o.tk}`)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={unitLabel}>{t("cart.notes")}</label>
                      <textarea
                        value={unit.notes}
                        onChange={(e) => setUnitField(i, "notes", e.target.value)}
                        placeholder={t("cart.notesPh")}
                        rows={2}
                        className={`${unitInput} resize-none`}
                        data-testid={`unit-notes-${n}`}
                      />
                    </div>
                  </div>
                );
              })}

              {error && (
                <p className="text-red-400 font-body text-sm" data-testid="order-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="btn-emerald w-full py-4 text-base font-semibold tracking-wider uppercase rounded-sm flex items-center justify-center gap-3"
                data-testid="order-submit-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                {t("order.addToCart")}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
